import {Button, Card, DataStatus, openDialog, showNotification} from 'platform/components';
import {Box, Center, Heading, Show, Text, VStack} from 'platform/foundation';

import {isEmpty} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  TenantMailboxResponseBody,
  UserMailboxResponseBody,
  useDeleteTenantMailboxMutation,
  useDeleteUserMailboxMutation,
  useListTenantMailboxesQuery,
  useListUserMailboxesQuery,
  useReauthenticateTenantMailboxMutation,
  useReauthenticateUserMailboxMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {buildUrl, userMailboxOAuthFinish} from '@dms/routes';
import {handleApiError, usePermissions} from '@dms/shared';

import {CreateMailboxDialog} from './CreateMailboxDialog';
import {MailboxCard} from './MailboxCard';

export function ReceivingMailboxesSection() {
  const [
    canReadTenantMailbox,
    canCreateTenantMailbox,
    canUpdateTenantMailbox,
    canDeleteTenantMailbox,
  ] = usePermissions({
    permissionKeys: [
      'tenantMailboxRead',
      'tenantMailboxCreate',
      'tenantMailboxUpdate',
      'tenantMailboxDelete',
    ],
  });

  const {data, isLoading, isError} = useListUserMailboxesQuery();
  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: isTenantError,
  } = useListTenantMailboxesQuery(undefined, {skip: !canReadTenantMailbox});

  const [deleteUserMailbox] = useDeleteUserMailboxMutation();
  const [reauthenticateUserMailbox] = useReauthenticateUserMailboxMutation();
  const [deleteTenantMailbox] = useDeleteTenantMailboxMutation();
  const [reauthenticateTenantMailbox] = useReauthenticateTenantMailboxMutation();

  const mailboxes: UserMailboxResponseBody[] = data?.mailboxes ?? [];
  const tenantMailboxes: TenantMailboxResponseBody[] = tenantData?.mailboxes ?? [];
  const connectedCount = mailboxes.filter((mailbox) => mailbox.status === 'CONNECTED').length;

  const handleCreate = () =>
    openDialog(<CreateMailboxDialog scope="user" />, {
      title: i18n.t('page.myProfile.receivingMailbox.createDialog.title'),
    });

  const handleCreateShared = () =>
    openDialog(<CreateMailboxDialog scope="tenant" />, {
      title: i18n.t('page.myProfile.receivingMailbox.createDialog.title'),
    });

  const handleDelete = async (id: string) => {
    await deleteUserMailbox({mailboxId: id})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('page.myProfile.receivingMailbox.notifications.removed'));
      })
      .catch(handleApiError);
  };

  const handleDeleteShared = async (id: string) => {
    await deleteTenantMailbox({mailboxId: id})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('page.myProfile.receivingMailbox.notifications.removed'));
      })
      .catch(handleApiError);
  };

  const handleReauthenticate = async (id: string) => {
    const redirectUri = buildUrl(userMailboxOAuthFinish, undefined, false);
    await reauthenticateUserMailbox({
      mailboxId: id,
      reauthenticateUserMailboxRequestBody: {redirectUri},
    })
      .unwrap()
      .then(({oauthUrl}) => {
        window.location.href = oauthUrl;
      })
      .catch(handleApiError);
  };

  const handleReauthenticateShared = async (id: string) => {
    const redirectUri = buildUrl(userMailboxOAuthFinish, undefined, false);
    await reauthenticateTenantMailbox({
      mailboxId: id,
      reauthenticateTenantMailboxRequestBody: {redirectUri},
    })
      .unwrap()
      .then(({oauthUrl}) => {
        window.location.href = oauthUrl;
      })
      .catch(handleApiError);
  };

  return (
    <VStack spacing={4}>
      <Text size="small" color="secondary">
        {i18n.t('page.myProfile.receivingMailbox.sectionDescription')}
      </Text>
      <VStack spacing={1}>
        <Heading size={4}>{i18n.t('page.myProfile.receivingMailbox.myMailbox')}</Heading>
        <Show when={isNotNilOrEmpty(mailboxes)}>
          <Text size="xSmall" color="secondary">
            {i18n.t('page.myProfile.receivingMailbox.connectedCount', {
              connected: connectedCount,
              total: mailboxes.length,
            })}
          </Text>
        </Show>
      </VStack>
      <DataStatus spacing={10} isLoading={isLoading} isError={isError}>
        {isEmpty(mailboxes) ? (
          <Card variant="inlineGrey">
            <Box paddingVertical={6}>
              <Center>
                <Button
                  variant="secondary"
                  leftIcon="content/add_circle"
                  title={i18n.t('page.myProfile.receivingMailbox.setupMyMailbox')}
                  onClick={handleCreate}
                />
              </Center>
            </Box>
          </Card>
        ) : (
          <VStack spacing={4}>
            {mailboxes.map((mailbox) => (
              <MailboxCard
                key={mailbox.id}
                mailbox={mailbox}
                onDelete={handleDelete}
                onReauthenticate={handleReauthenticate}
              />
            ))}
          </VStack>
        )}
      </DataStatus>
      <Show when={canReadTenantMailbox}>
        <VStack spacing={1}>
          <Heading size={4}>{i18n.t('page.myProfile.receivingMailbox.sharedMailboxes')}</Heading>
          <Text size="xSmall" color="secondary">
            {i18n.t('page.myProfile.receivingMailbox.sharedMailboxesDescription')}
          </Text>
        </VStack>
        <DataStatus
          spacing={10}
          isLoading={isTenantLoading}
          isError={isTenantError}
          isEmpty={isEmpty(tenantMailboxes) && !canCreateTenantMailbox}
        >
          {isEmpty(tenantMailboxes) ? (
            <Card variant="inlineGrey">
              <Box paddingVertical={6}>
                <Center>
                  <Button
                    variant="secondary"
                    leftIcon="content/add_circle"
                    title={i18n.t('page.myProfile.receivingMailbox.connectSharedMailbox')}
                    onClick={handleCreateShared}
                  />
                </Center>
              </Box>
            </Card>
          ) : (
            <VStack spacing={4}>
              {tenantMailboxes.map((mailbox) => (
                <MailboxCard
                  key={mailbox.id}
                  mailbox={mailbox}
                  onDelete={canDeleteTenantMailbox ? handleDeleteShared : undefined}
                  onReauthenticate={canUpdateTenantMailbox ? handleReauthenticateShared : undefined}
                />
              ))}
              <Show when={canCreateTenantMailbox}>
                <Box>
                  <Button
                    variant="secondary"
                    leftIcon="content/add_circle"
                    title={i18n.t('page.myProfile.receivingMailbox.connectSharedMailbox')}
                    onClick={handleCreateShared}
                    isFullWidth
                  />
                </Box>
              </Show>
            </VStack>
          )}
        </DataStatus>
      </Show>
    </VStack>
  );
}
